import { atom } from "jotai";

import { Logger } from "@moment/logging";

import { $accessTokenFunc } from "../authenication";
import { $docsApi } from "../docsapi";
import { type DocsListItem, docsListItemRemoteEntry } from "../document-list";
import { MetadataLoadError } from "./errors";
import { $loadDocumentMetadata, $setMetadataStateError } from "./metadata-state.atom";

const logger = new Logger("load-metadata-from-remote");

export interface LoadDocumentMetadataFromRemoteInput {
	key: string;
	document: DocsListItem;
	ssr: boolean;
}

/**
 * Loads document metadata from the remote DocsAPI (git.moment.dev).
 */
export const $loadDocumentMetadataFromRemote = atom(
	null,
	async (get, set, input: LoadDocumentMetadataFromRemoteInput) => {
		const { key, document, ssr } = input;
		const documentId = document.id;

		try {
			// Get the remote entry from the document.
			const workspaceName = docsListItemRemoteEntry(document)?.workspace.name;
			if (!workspaceName) {
				throw new Error("Document has no remote workspace");
			}

			// Get access token if available.
			const { getAccessTokenSilently } = get($accessTokenFunc);
			const token = getAccessTokenSilently ? await getAccessTokenSilently() : undefined;

			// Fetch metadata from DocsAPI.
			const readMetadata = get($docsApi).document.metadata.read;
			const metadata = await readMetadata({ documentId, workspaceName }, token);
			if (!metadata) {
				throw new Error("No metadata returned from remote");
			}

			// Load the metadata state.
			set($loadDocumentMetadata, { key, metadata, ssr });
		} catch (err) {
			const error = new MetadataLoadError(err);
			logger.error("Failed to load metadata from remote", { documentId, err });
			set($setMetadataStateError, { key, error });
		}
	}
);
$loadDocumentMetadataFromRemote.debugLabel = "loadDocumentMetadataFromRemote";
